/**
 * Side-rail "Recent PCOs" — recent means RECENT (Brandon's definition,
 * 2026-07-10): activity inside the window (default 28 days) or a newly
 * created PCO. No filler — if two PCOs qualify, the list is two. The full
 * directory lives in the rail's All-PCOs flyout.
 *
 * @see PcoBillingType — the eligibility gate described below.
 *
 * `billing_type` gates entry BEFORE any ranking: a PCO explicitly marked
 * `lump_sum` never appears here. Such a PCO is priced outside the portal and
 * has no weekly workflow, so a rail slot would never lead anywhere — it lives
 * in the All-PCOs flyout and on the PCO Log instead. The gate applies to the
 * current-PCO pin too, so viewing a lump-sum PCO simply leaves the rail
 * unhighlighted rather than smuggling it into a "recent weekly work" list.
 * A PCO with no `billing_type` at all (an API older than the column) is kept —
 * the gate fails open so a deploy-skew window degrades to the old behavior
 * instead of an empty rail.
 *
 * A PCO qualifies when any of:
 *  - `has_unreviewed_blocks` — new work just landed, nobody has opened the PCO since
 *  - `open_week_count > 0` — a week is being billed right now
 *  - `latest_week_ending_date` inside the window — recently billed work
 *  - `created_at` inside the window — newly created PCO
 *
 * Order: attention first, then open weeks, then latest activity descending.
 * The currently-viewed PCO is appended if it didn't qualify so the rail's
 * active highlight always has a target.
 */
import type { PcoBillingType } from './status';

export interface RailRankablePco {
  id: string;
  pco_number: string;
  /**
   * Only `lump_sum` PCOs are excluded from the rail. Optional on purpose —
   * an API deployed before the column exists sends nothing, and the rail must
   * degrade to "show everything" rather than to empty. See the filter.
   */
  billing_type?: PcoBillingType;
  has_unreviewed_blocks: boolean;
  open_week_count: number;
  latest_week_ending_date: string | null;
  /** ISO timestamp. Backfilled PCOs carry a normalized pre-window date so the
   *  import batch doesn't read as "just created". */
  created_at: string;
}

const DEFAULT_WINDOW_DAYS = 28;
/** Backstop only — with a real recency filter the list stays naturally short. */
const DEFAULT_CAP = 15;
const DAY_MS = 24 * 60 * 60 * 1000;

function bucket(p: RailRankablePco): number {
  if (p.has_unreviewed_blocks) return 0;
  if (p.open_week_count > 0) return 1;
  return 2;
}

/** Latest signal we have for "something happened here", in epoch ms. */
function activityMs(p: RailRankablePco): number {
  const week = p.latest_week_ending_date ? Date.parse(p.latest_week_ending_date) : Number.NEGATIVE_INFINITY;
  const created = Date.parse(p.created_at);
  return Math.max(week, Number.isFinite(created) ? created : Number.NEGATIVE_INFINITY);
}

export function recentRailPcos<T extends RailRankablePco>(
  pcos: readonly T[],
  opts: { nowMs: number; currentPcoId?: string | null; windowDays?: number; cap?: number },
): T[] {
  const cutoff = opts.nowMs - (opts.windowDays ?? DEFAULT_WINDOW_DAYS) * DAY_MS;
  const cap = opts.cap ?? DEFAULT_CAP;

  // Eligibility gate, applied before ranking AND before the current-PCO pin
  // below — a lump-sum PCO has no weekly workflow to be recent about.
  //
  // EXCLUDE 'lump_sum' rather than REQUIRE 'weekly_rollup', deliberately: the
  // web app can be running against an API that predates the column (Pages
  // deploys on push, the Worker only on an explicit `wrangler deploy`), and
  // those responses carry no billing_type at all. Requiring the positive value
  // would empty the rail entirely during that window. Only an explicit
  // 'lump_sum' hides a PCO; anything else — including missing — stays.
  const eligible = pcos.filter((p) => p.billing_type !== 'lump_sum');

  const recent = eligible
    .filter((p) => p.has_unreviewed_blocks || p.open_week_count > 0 || activityMs(p) >= cutoff)
    .sort((a, b) => {
      const ba = bucket(a);
      const bb = bucket(b);
      if (ba !== bb) return ba - bb;
      const d = activityMs(b) - activityMs(a);
      if (d !== 0) return d;
      return b.pco_number.localeCompare(a.pco_number, undefined, { numeric: true });
    })
    .slice(0, cap);

  const currentId = opts.currentPcoId;
  if (currentId && !recent.some((p) => p.id === currentId)) {
    const current = eligible.find((p) => p.id === currentId);
    if (current) recent.push(current);
  }
  return recent;
}
